import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { UiIcons } from '@pipebase/ui';
import { useFavorites, type FavoriteKind } from '../hooks/useFavorites';

const KIND_LABEL: Record<FavoriteKind, string> = {
  flow: 'F',
  'event-design': 'E',
  'value-mapping': 'V',
  'message-mapping': 'M',
  connection: 'C',
  runtime: 'R',
  parameter: 'P',
};

function kindGlyph(kind: FavoriteKind) {
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 16,
        height: 16,
        fontSize: 10,
        fontWeight: 700,
        color: 'var(--text-3)',
        background: 'var(--bg-surface-2)',
        borderRadius: 4,
      }}
    >
      {KIND_LABEL[kind]}
    </span>
  );
}

function favHref(kind: FavoriteKind, id: string): string {
  switch (kind) {
    case 'flow':
      return `/edit-next/${encodeURIComponent(id)}`;
    case 'event-design':
      return `/event-design/${encodeURIComponent(id)}`;
    case 'value-mapping':
      return `/value-mappings/${encodeURIComponent(id)}`;
    case 'message-mapping':
      return `/mappings/${encodeURIComponent(id)}`;
    case 'connection':
      return `/connections#${encodeURIComponent(id)}`;
    case 'runtime':
      return `/runtimes/${encodeURIComponent(id)}`;
    case 'parameter':
      return `/parameters#${encodeURIComponent(id)}`;
  }
}

/**
 * Favorites section rendered inside the sidebar scroll area.
 * Extracted from Sidebar.tsx so the package Sidebar can accept it as a
 * `favorites` ReactNode slot — keeping the SPA-specific hook dependencies
 * out of @pipebase/ui.
 */
export function FavoritesSection() {
  const [pinnedOpen, setPinnedOpen] = useState(true);
  const { items: favorites } = useFavorites();

  if (favorites.length === 0) return null;

  return (
    <div className={`sidebar__group ${!pinnedOpen ? 'is-collapsed' : ''}`}>
      <button
        type="button"
        className="sidebar__group-header"
        onClick={() => setPinnedOpen((o) => !o)}
        data-testid="sidebar-pinned-header"
      >
        <span className="sidebar__group-chevron">{UiIcons.chevronDown}</span>
        <span>Pinned</span>
      </button>
      <div className="sidebar__group-list">
        {favorites.map((f) => (
          <NavLink
            key={`fav-${f.kind}-${f.id}`}
            to={favHref(f.kind, f.id)}
            className="nav-item"
            data-testid="sidebar-pinned-item"
            title={f.title}
          >
            <span className="nav-item__icon">{kindGlyph(f.kind)}</span>
            <span className="nav-item__label">{f.title}</span>
          </NavLink>
        ))}
      </div>
    </div>
  );
}
